// JSON 문자열에 구문 색을 입히는 컴포넌트
import classes from './DescriptionPageTemplate.module.css';

/**
 * json-format 의 들여쓰기 설정.
 *
 * 기본값이 `{ type: 'tab' }` 이라 `\t` 한 개를 넣는데, 브라우저 기본 탭 폭이 8칸이라
 * 예시가 과하게 벌어진다. 시안(preview.html)은 2칸이다.
 */
export const JSON_INDENT = { type: 'space', size: 2 };

/**
 * JSON 토큰 패턴.
 *
 * 1) 문자열 리터럴 + 뒤따르는 콜론 (있으면 키, 없으면 값)
 * 2) true·false·null 리터럴
 * 3) 숫자 (소수·지수 표기 포함)
 * 4) 구두점
 *
 * 매칭되지 않은 구간(공백·줄바꿈·들여쓰기)은 그대로 통과시킨다. 빠뜨리면 정렬이 깨진다.
 */
const TOKEN_PATTERN = /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],:])/g;

/**
 * JSON 문자열을 토큰별 span 으로 나눈다.
 *
 * dangerouslySetInnerHTML 을 쓰지 않는다. API TEST 의 Send 응답도 같은 경로를 타게 될 수 있어
 * 문자열을 HTML 로 해석하지 않는 편이 안전하다.
 *
 * @param {string} value - 포맷된 JSON 문자열
 * @returns {Array<JSX.Element|string>} 색이 입혀진 노드 배열
 */
function tokenize(value) {
  const nodes = [];
  let lastIndex = 0;
  let match;

  TOKEN_PATTERN.lastIndex = 0;
  while ((match = TOKEN_PATTERN.exec(value)) !== null) {
    const [raw, string, colon, literal, number, punct] = match;

    // 토큰 사이의 공백·줄바꿈은 색 없이 그대로 둔다
    if (match.index > lastIndex) {
      nodes.push(value.slice(lastIndex, match.index));
    }

    const key = `${match.index}`;
    if (string !== undefined) {
      // 뒤에 콜론이 오면 키다
      nodes.push(
        <span key={key} className={colon ? classes.tokKey : classes.tokStr}>
          {string}
        </span>
      );
      if (colon) {
        // 콜론과 그 앞 공백은 구두점 색으로
        nodes.push(
          <span key={`${key}c`} className={classes.tokPunct}>
            {colon}
          </span>
        );
      }
    } else if (literal !== undefined || number !== undefined) {
      // true·false·null 은 숫자와 같은 색을 쓴다. 시안에 전용 클래스가 없고 리터럴이라는 성질이 같다
      nodes.push(
        <span key={key} className={classes.tokNum}>
          {literal !== undefined ? literal : number}
        </span>
      );
    } else if (punct !== undefined) {
      nodes.push(
        <span key={key} className={classes.tokPunct}>
          {punct}
        </span>
      );
    }

    lastIndex = match.index + raw.length;
  }

  if (lastIndex < value.length) {
    nodes.push(value.slice(lastIndex));
  }
  return nodes;
}

/**
 * 색이 입혀진 JSON 코드.
 *
 * @param {string} value - 포맷된 JSON 문자열
 * @param {string} className - 감쌀 pre 의 클래스
 */
const JsonCode = ({ value, className }) => (
  <pre className={className}>{value ? tokenize(value) : value}</pre>
);

export default JsonCode;
